// @flow
import React from 'react';
import injectSheet from 'react-jss';
import Icon from '@ff/ui/Icon';
import { FF_ICONS } from 'shared/ff-icons';
import Dropdown from './Dropdown';
import IconGallery from './IconGallery';
import Button from '~/app/ui/Button';
import { S, A, DARK } from '~/app/ui/tokens';

type TextWithIcon = {
  text: string,
  icon: string,
  placement: 'BEFORE' | 'AFTER',
  icon_size: string,
}
type Props = {
  classes: Object,
  type: String,
  placeholder: String,
  value: TextWithIcon,
  onChange: () => void,
  disabled: Boolean
};

class TextEditor extends React.Component {
  props: Props = this.props;
  state = {
    iconGalleryOpen: false
  };

  handleTextChange = (text) => {
    const newVal = {
      ...this.props.value,
      text
    };

    this.props.onChange(newVal);
  }

  handleRemoveIcon = () => {
    const newVal = {
      text: this.props.value.text,
      icon: '',
      placement: ''
    };

    this.props.onChange(newVal);
  }

  handleAddIcon = () => {
    const newVal = {
      text: this.props.value.text,
      icon: 'arrow_right',
      placement: 'AFTER'
    };

    this.props.onChange(newVal);
  }

  handleEditIcon = (iconClassName) => {
    const newVal = {
      ...this.props.value,
      icon: iconClassName,
      placement: this.props.value.placement || 'BEFORE'
    };

    this.props.onChange(newVal);
    this.setState({ iconGalleryOpen: false });
  }

  handlePlacementChange = (val) => {
    const newVal = {
      ...this.props.value,
      placement: val
    };

    this.props.onChange(newVal);
  }

  handleIconChange = (val) => {
    const newVal = {
      ...this.props.value,
      icon_size: val
    };

    this.props.onChange(newVal);
  }

  render() {
    const { classes, type, placeholder, value, disabled } = this.props;
    const options = [
      'BEFORE',
      'AFTER'
    ];

    return (
      <div className={classes.container}>
        <input
          type={type}
          className={classes.textInput}
          placeholder={placeholder}
          value={value.text}
          onChange={(e) => this.handleTextChange(e.target.value)}
          disabled={disabled}
        />
        {value.icon && (
          <div className={classes.withIcon}>
            <div
              className={classes.iconContainer}
              onClick={() => this.setState({ iconGalleryOpen: true })}
            >
              <Icon name={value.icon} className={classes.icon} />
            </div>
            <div className={classes.dropdown}>
              <Dropdown
                options={options}
                value={value.placement}
                onChange={(val) => this.handlePlacementChange(val)}
              />
            </div>
            <div className={classes.iconSizeContainer} >
              <input
                className={classes.iconSizeInput}
                placeholder="Icon Size"
                value={value.icon_size}
                onChange={(e) => this.handleIconChange(e.target.value)}
              />
            </div>
            <Button size="sm" icon="remove" className={classes.button} onClick={() => this.handleRemoveIcon()}>
              Remove icon
            </Button>
          </div>
        )}
        {!value.icon && (
          <Button size="sm" icon="add" className={classes.button} onClick={this.handleAddIcon}>
            Add icon
          </Button>
        )}
        {this.state.iconGalleryOpen && (
          <IconGallery
            icons={FF_ICONS}
            onClose={() => this.setState({ iconGalleryOpen: false })}
            onSave={this.handleEditIcon}
          />
        )}
      </div>
    );
  }
}

const styles = {
  container: {
    width: '100%',
    display: 'flex',
  },
  textInput: {
    marginLeft: '10px',
    border: '1px solid hsl(0, 0%, 90%)',
    padding: '10px 10px',
    borderRadius: '3px',
    fontSize: '13px',
    color: 'hsl(0, 0%, 20%)',
    fontWeight: '300',
    width: '100%',
    outline: 'none',
    '&:disabled': {
      cursor: 'not-allowed',
      backgroundColor: 'hsl(0, 0%, 90%)',
      color: 'hsl(0, 0%, 73%)',
    },
    [DARK]: {
      borderColor: S.fieldLine,
      backgroundColor: S.field,
      color: S.fg,
      '&:disabled': { backgroundColor: S.chromeHover, color: S.muted }
    }
  },
  withIcon: {
    display: 'flex',
  },
  dropdown: {},
  iconContainer: {
    height: '38px',
    paddingLeft: '18px',
    paddingRight: '18px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: '.2s all ease',
    borderRadius: '3px',
    backgroundColor: 'hsl(0, 0%, 98%)',
    '&:hover': {
      backgroundColor: 'hsl(0, 0%, 95%)',
      boxShadow: '0px 3px 8px 1px hsl(0, 0%, 80%)',
    },
    [DARK]: { backgroundColor: S.chrome, '&:hover': { backgroundColor: S.chromeHover, boxShadow: A.pop } }
  },
  icon: {
    fontSize: '12px',
  },
  button: {
    flex: '0 0 auto',
    alignSelf: 'center',
    marginLeft: 8
  },
  iconSizeInput: {
    width: '50px',
    height: '25px',
    fontSize: '10px',
  },
  iconSizeContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px',
  },
};

export default injectSheet(styles)(TextEditor);
